import React from 'react';
import { Monitor, X, Check, Inbox } from 'lucide-react';
import { PREVIEW_LABEL_RATIO, PREVIEW_BODY_RATIO, PREVIEW_SONG_BODY_RATIO, PREVIEW_WELCOME_RATIO, PREVIEW_LABEL_MARGIN } from './previewRatios';

// "Slide 1 of 6": the preview shows the first slide; the congregation sees them one at a time.
const SlideCount = ({ slides }) => (Array.isArray(slides) && slides.length > 1 ? (
  <div className="mt-2 text-xs font-normal opacity-80" style={{ fontFamily: 'inherit' }}>Slide 1 of {slides.length}</div>
) : null);

/** The orange Staging card: what is queued for the projector, with Plan / Live / clear actions. */
const StagingCard = ({
  dm, stagedContent, slides, settings, previewBg, previewText, isLoadingVerse,
  addStagedToRunSheet, sendStagedContentLive, clearStagedContent,
}) => (
    <div className={`rounded-2xl p-4 border-2 shadow-lg ring-2 ring-amber-500/20 ${dm ? 'bg-gradient-to-br from-orange-900/40 to-amber-900/40 border-orange-400/50' : 'bg-white border-orange-300'}`}>
      <div className="flex items-center justify-between mb-2">
        <h2 className={`text-base font-bold flex items-center gap-2 ${dm ? 'bg-gradient-to-r from-orange-200 to-amber-200 bg-clip-text text-transparent' : 'text-orange-600'}`}>
          <div className="p-1.5 bg-gradient-to-br from-orange-500 to-amber-500 rounded-lg">
            <Monitor className="w-3.5 h-3.5 text-slate-50" />
          </div>
          Staging
        </h2>
        {(stagedContent.type === 'scripture' || stagedContent.type === 'song') && (
          <div className="flex gap-1">
            {!stagedContent.custom && (
              <button
                onClick={addStagedToRunSheet}
                title="Add this to the run-sheet"
                className="flex items-center gap-0.5 bg-slate-600 hover:bg-slate-500 text-slate-50 font-bold py-1 px-2 rounded-lg text-xs shadow-lg transition-all"
              >
                + Plan
              </button>
            )}
            <button
              onClick={sendStagedContentLive}
              className="flex items-center gap-0.5 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-slate-50 font-bold py-1 px-2 rounded-lg text-xs shadow-lg transition-all transform hover:scale-105"
            >
              <Check className="w-3 h-3" />
              Live
            </button>
            <button
              onClick={clearStagedContent}
              className="flex items-center gap-0.5 border border-red-500/40 bg-red-500/10 text-red-500 hover:bg-red-600 hover:text-white font-bold py-1 px-2 rounded-lg text-xs transition-all"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        )}
      </div>
      <div
        className="rounded-lg overflow-hidden shadow-lg flex items-center justify-center p-4 border border-orange-400/20"
        style={{
          ...previewBg.style,
          color: settings.fontColor,
          minHeight: '120px',
          maxHeight: '280px',
          position: 'relative'
        }}
      >
        {previewBg.dim > 0 && (
          <div className="absolute inset-0" style={{ background: `rgba(0,0,0,${previewBg.dim})` }} />
        )}
        <div className="text-center max-w-full relative" style={previewText}>
          {stagedContent.type === 'scripture' ? (
            <div>
              <div style={{ fontSize: `${settings.fontSize * PREVIEW_LABEL_RATIO}px`, fontFamily: settings.fontFamily, fontWeight: 'bold', marginBottom: PREVIEW_LABEL_MARGIN }}>
                {stagedContent.reference} {stagedContent.version && `(${stagedContent.version})`}
              </div>
              <div style={{ fontSize: `${settings.fontSize * PREVIEW_BODY_RATIO}px`, fontFamily: settings.fontFamily, lineHeight: '1.4' }}>
                {isLoadingVerse && stagedContent.content === 'Loading...' ? (
                  <span className="animate-pulse">⏳ Loading...</span>
                ) : (
                  Array.isArray(slides) && slides.length > 0 ? slides[0] : stagedContent.content
                )}
              </div>
              <SlideCount slides={slides} />
            </div>
          ) : stagedContent.type === 'song' ? (
            <div>
              <div style={{ fontSize: `${settings.fontSize * PREVIEW_SONG_BODY_RATIO}px`, fontFamily: settings.fontFamily, fontWeight: 'bold', marginBottom: PREVIEW_LABEL_MARGIN }} className="text-emerald-600">
                {stagedContent.title}
              </div>
              <div style={{ fontSize: `${settings.fontSize * PREVIEW_SONG_BODY_RATIO}px`, fontFamily: settings.fontFamily, lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                {Array.isArray(slides) && slides.length > 0 ? slides[0] : stagedContent.content}
              </div>
              <SlideCount slides={slides} />
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2 text-orange-300/70">
              <Inbox className="w-8 h-8" strokeWidth={1.5} />
              <div style={{ fontSize: `${settings.fontSize * PREVIEW_WELCOME_RATIO}px`, fontFamily: settings.fontFamily, fontWeight: 'bold' }}>
                Empty
              </div>
              <p className="text-xs font-normal opacity-80">Stage a verse or song to preview it here</p>
            </div>
          )}
        </div>
      </div>
    </div>
);

export default StagingCard;
